// ─── What: SignalItem -> PreparedSignal ───────────────────────────────────────
//
// Normalisation only: unpack the loose `fact` / `session` bags, resolve the
// colour, and attach screen coordinates. Pure given the two positioning
// functions, so it can be tested without a DOM.
//
// A signal outside the visible window, or on a market with no line to sit on, is
// dropped here rather than drawn off-plot.

import type { SignalItem } from '../../../../api/types';
import type { PreparedSignal } from '../../chartTypes';

export interface PrepareSignalsInput {
    byMarket: Map<string, SignalItem[]>;
    /** Per-market fallback colour when the type has none. */
    colorMap: Map<string, string>;
    /** Signal-type colours, from buildSignalTypeColors. */
    typeColors: Record<string, string>;
    /** Last resort when neither map has a colour. */
    fallbackColor: string;
    xDomain: [Date, Date];
    x: (d: Date) => number;
    /** Screen y of a market's probability line at a date, or null if unknown. */
    getProbY: (conditionId: string, date: Date) => number | null;
}

export function prepareSignals(input: PrepareSignalsInput): PreparedSignal[] {
    const { byMarket, colorMap, typeColors, fallbackColor, xDomain, x, getProbY } =
        input;
    const prepared: PreparedSignal[] = [];

    for (const [conditionId, signals] of byMarket) {
        for (const sig of signals) {
            const openedAt = new Date(sig.opened_at);
            if (openedAt < xDomain[0] || openedAt > xDomain[1]) continue;
            const py = getProbY(conditionId, openedAt);
            if (py === null) continue;

            const fact = (sig.fact ?? {}) as Record<string, unknown>;
            const session = (sig.session ?? {}) as Record<string, unknown>;
            // Some signal types carry the interesting numbers on the session's
            // opening fact rather than the current one.
            const openingFact = (session.opening_fact ?? {}) as Record<
                string,
                unknown
            >;

            prepared.push({
                id: sig.id,
                conditionId,
                openedAt,
                closedAt: sig.closed_at ? new Date(sig.closed_at) : null,
                lastSeenAt: sig.last_seen_at
                    ? new Date(sig.last_seen_at)
                    : null,
                direction: sig.direction,
                signalType: sig.signal_type,
                traderGroup: sig.trader_group,
                status: sig.status,
                score: Math.max(0, Math.min(1, sig.score)),
                color:
                    typeColors[sig.signal_type] ??
                    colorMap.get(conditionId) ??
                    fallbackColor,
                py,
                px: x(openedAt),
                address: sig.address ?? null,
                addressAlias: sig.address_alias ?? null,
                factDeltaUsd: (fact.delta_usd ??
                    openingFact.delta_usd ??
                    null) as number | null,
                factSide: (fact.side ?? openingFact.side ?? null) as
                    | string
                    | null,
                sessionOpeningPrice: (session.opening_price ?? null) as
                    | number
                    | null,
                sessionPeakPrice: (session.peak_price ?? null) as number | null,
                sessionClosePrice: (session.close_price ?? null) as
                    | number
                    | null,
                windowMinutes:
                    sig.signal_type === 'volume_anomaly'
                        ? (fact.window_minutes as number) ?? null
                        : null,
            });
        }
    }

    return prepared;
}
